import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike } from '@krp/client-runtime'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { formatDateTime } from '../../pricing/model/format'
import { mutationState, type MutationLike } from '../../pricing/model/viewKit'
import type { CreateDefaults, CreateDefaultsDraft, CreateDefaultsFieldVm, CreateDefaultsIntentState, CreateDefaultsVm } from './types'

export type CreateDefaultsResult = QueryResultLike<OneCEnvelope<CreateDefaults>>

export interface CreateDefaultsScreenInput {
  resourceId: string
  query: CreateDefaultsResult
  intent: CreateDefaultsIntentState
  save: MutationLike
}

const NO_DRAFT: CreateDefaultsDraft = {}

/** Значения к сохранению: поле — его значение в окне (`''` → `null`, умолчания нет). */
export function createDefaultsValues(fields: readonly CreateDefaultsFieldVm[]): Record<string, string | null> {
  return Object.fromEntries(fields.map((field) => [field.key, field.value === '' ? null : field.value]))
}

/**
 * vm окна «Создание по умолчанию»: (ответ службы + черновик базы) → поля с
 * выбранными значениями. Значение поля — из черновика, а без правки —
 * сохранённое умолчание. Обязательное поле без значения помечено словами:
 * создание из синхронизации без выбора при применении откажет.
 */
export function makeSelectCreateDefaultsVm() {
  return createSelector(
    [
      (input: CreateDefaultsScreenInput) => input.query,
      (input: CreateDefaultsScreenInput) => input.intent[normalizeRid(input.resourceId)] ?? NO_DRAFT,
      (input: CreateDefaultsScreenInput) => input.save,
    ],
    (query, draft, save): CreateDefaultsVm => {
      const view = queryView(query, (read) => read.data.fields.length === 0)
      const data = view.status === 'ready' ? query.data?.data : undefined
      const staleKeys = new Set(data?.stale.map((item) => item.key) ?? [])
      const fields = (data?.fields ?? []).map((field): CreateDefaultsFieldVm => {
        const value = draft[field.key] ?? field.defaultValue ?? ''
        return {
          key: field.key,
          label: field.label,
          required: field.required,
          value,
          options: field.options,
          missing: field.required && value === '',
          stale: staleKeys.has(field.key) && draft[field.key] === undefined,
        }
      })
      return {
        query: view,
        fields,
        lostFields: (data?.stale ?? []).filter((item) => !fields.some((field) => field.key === item.key)).map((item) => item.label),
        savedAt: data?.updatedAt === null || data?.updatedAt === undefined ? null : formatDateTime(data.updatedAt),
        savedBy: data?.updatedBy ?? null,
        dirty: Object.keys(draft).length > 0,
        save: mutationState(save),
      }
    },
  )
}
